import {
  FishingReport,
  RecommendedProductFamily,
  Technique,
  WaterbodyDetail,
} from '@omniafishing/core';
import _ from 'lodash';
import React from 'react';
import { useSelector } from 'react-redux';
import { useListAttributionUuid } from '../../../../hooks/use_list_attribution_uuid';
import { LINE_ATTRIBUTION } from '../../../../redux/cart';
import { getTechniqueByTechniqueName } from '../../../../redux/reference_data';
import { ProductCarousel } from '../../../carousel/product_carousel';
import { ProductsGroupCard } from '../../../products_group_card/products_group_card';
import {
  baitCategoryNames,
  lineTerminalCategoryNames,
  rodsReelsCategoryNames,
} from '../../waterbody_detail_lib/get_top_products';
import styles from './technique_carousels.less';

interface TechniqueCarouselsProps {
  fishingReports: FishingReport[];
  recommendedProductFamilies: RecommendedProductFamily[];
  waterbodyDetail: WaterbodyDetail;
  selectedTechnique: Technique | null;
}

export const TechniqueCarousels = React.memo((props: TechniqueCarouselsProps) => {
  const { recommendedProductFamilies, fishingReports, waterbodyDetail, selectedTechnique } = props;
  const techniqueByTechniqueName = useSelector(getTechniqueByTechniqueName);

  const list_uuid = useListAttributionUuid();

  const baits = recommendedProductFamilies.filter((rf) =>
    baitCategoryNames.includes(rf.product_family.category)
  );
  const lineTerminal = recommendedProductFamilies.filter((rf) =>
    lineTerminalCategoryNames.includes(rf.product_family.category)
  );
  const rodsReels = recommendedProductFamilies.filter((rf) =>
    rodsReelsCategoryNames.includes(rf.product_family.category)
  );

  const techniqueFishingReports = selectedTechnique
    ? fishingReports.filter((fr) => {
        return fr.technique.name === selectedTechnique?.name;
      })
    : fishingReports;

  const RecommendedFor = (
    <p className={styles.recommendedFor}>
      {`Recommended for ${
        techniqueByTechniqueName(selectedTechnique?.name)?.display_name_alternate
      }`}
    </p>
  );

  return (
    <div className={styles.wrapper}>
      {baits.length > 0 && (
        <>
          <h3>Top Baits ({baits.length})</h3>
          {RecommendedFor}
          <div className={styles.carousel}>
            <ProductCarousel
              adaptive
              products={_.flatten(baits.map((rf) => rf.products))}
              arrows={true}
              list_id_title={`Top Baits`}
              list_uuid={list_uuid}
            >
              {baits.map((recommendedFamily, i) => {
                return (
                  <div
                    key={recommendedFamily.product_family.handle}
                    className={styles.carouselProduct}
                  >
                    <ProductsGroupCard
                      products={recommendedFamily.products}
                      position={i}
                      fishingReports={techniqueFishingReports}
                      list_uuid={list_uuid}
                      showTechniqueReported={false}
                      lineItemAttribution={[
                        {
                          key: LINE_ATTRIBUTION.WATERBODY_ID,
                          value: waterbodyDetail.id,
                        },
                      ]}
                      price_pro={recommendedFamily.product_family.price_pro}
                    />
                  </div>
                );
              })}
            </ProductCarousel>
          </div>
        </>
      )}
      {lineTerminal.length > 0 && (
        <>
          <h3>Top Line and Terminal ({lineTerminal.length})</h3>
          {RecommendedFor}
          <div className={styles.carousel}>
            <ProductCarousel
              adaptive
              products={_.flatten(lineTerminal.map((rf) => rf.products))}
              arrows={true}
              list_id_title={`Top Line and Terminal`}
              list_uuid={list_uuid}
            >
              {lineTerminal.map((recommendedFamily, i) => {
                return (
                  <div
                    key={recommendedFamily.product_family.handle}
                    className={styles.carouselProduct}
                  >
                    <ProductsGroupCard
                      products={recommendedFamily.products}
                      position={i}
                      fishingReports={fishingReports}
                      list_uuid={list_uuid}
                      lineItemAttribution={[
                        {
                          key: LINE_ATTRIBUTION.WATERBODY_ID,
                          value: waterbodyDetail.id,
                        },
                      ]}
                      price_pro={recommendedFamily.product_family.price_pro}
                    />
                  </div>
                );
              })}
            </ProductCarousel>
          </div>
        </>
      )}

      {rodsReels.length > 0 && (
        <>
          <h3>Top Rods and Reels ({rodsReels.length})</h3>
          {RecommendedFor}
          <div className={styles.carousel}>
            <ProductCarousel
              adaptive
              products={_.flatten(rodsReels.map((rf) => rf.products))}
              arrows={true}
              list_id_title={`Top Rods and Reels`}
              list_uuid={list_uuid}
            >
              {rodsReels.map((recommendedFamily, i) => {
                return (
                  <div
                    key={recommendedFamily.product_family.handle}
                    className={styles.carouselProduct}
                  >
                    <ProductsGroupCard
                      products={recommendedFamily.products}
                      position={i}
                      fishingReports={fishingReports}
                      list_uuid={list_uuid}
                      lineItemAttribution={[
                        {
                          key: LINE_ATTRIBUTION.WATERBODY_ID,
                          value: waterbodyDetail.id,
                        },
                      ]}
                      price_pro={recommendedFamily.product_family.price_pro}
                    />
                  </div>
                );
              })}
            </ProductCarousel>
          </div>
        </>
      )}
    </div>
  );
});
